import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {useNavigate, useParams} from "react-router-dom";
import {baseUrl, ipAddress, userName,} from "../Univ.js";
import "./IoTStyles.css";
function IoTDeviceForm() {
    const navigate = useNavigate();
    const {id} = useParams();
    const [loading, setLoading] = useState(false);
    const [gateways, setGateways] = useState([]);
    // ============================================================
    // FORM DATA
    // ============================================================
    const [formData, setFormData] = useState({
        device_id: "",
        device_name: "",
        device_type: "SOIL_MOISTURE",
        manufacturer: "",
        model_number: "",
        serial_number: "",
        firmware_version: "",
        protocol: "MQTT",
        ip_address: "",
        port: "",
        mac_address: "",
        gateway: "",
        status: "ACTIVE",
        installation_date: "",
        warranty_expiry: "",
        calibration_due: "",
        service_due: ""
    });
    // ============================================================
    // INITIAL LOAD
    // ============================================================
    useEffect(() => {
        fetchGateways();
        if (id) {
            fetchDevice();
        }
    }, [id]);
    // ============================================================
    // FETCH GATEWAYS
    // ============================================================
    const fetchGateways = async () => {
        try {
            const response = await axios.get(baseUrl + "iot-gateways/");
            setGateways(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.log("Gateway Error:", error);
        }
    };
    // ============================================================
    // FETCH DEVICE
    // ============================================================
    const fetchDevice = async () => {
        try {
            setLoading(true);
            const response = await axios.get(baseUrl + "iot-devices/" + id + "/");
            setFormData({
                ...formData, ...response.data, gateway: response.data.gateway || ""
            });
        } catch (error) {
            console.log("IoT Device Error:", error);
            Swal.fire("Error", "Unable to load IoT device.", "error");
        } finally {
            setLoading(false);
        }
    };
    // ============================================================
    // INPUT HANDLER
    // ============================================================
    const inputHandler = (event) => {
        const {
            name, value
        } = event.target;
        setFormData(previous => ({
            ...previous, [name]: value
        }));
    };
    // ============================================================
    // SUBMIT
    // ============================================================
    const submitHandler = async (event) => {
        event.preventDefault();
        // --------------------------------------------------------
        // REQUIRED VALIDATION
        // --------------------------------------------------------
        if (!formData.device_id || !formData.device_name || !formData.device_type || !formData.protocol) {
            Swal.fire("Required", "Please fill all mandatory fields.", "warning");
            return;
        }
        try {
            setLoading(true);
            // ----------------------------------------------------
            // PAYLOAD
            // ----------------------------------------------------
            const payload = {
                ...formData,
                gateway: formData.gateway === "" ? null : formData.gateway,
                addedby: userName,
                ip_add: ipAddress
            };
            let response;
            // ----------------------------------------------------
            // UPDATE
            // ----------------------------------------------------
            if (id) {
                response = await axios.put(baseUrl + "iot-devices/" + id + "/", payload);
            }

                // ----------------------------------------------------
                // CREATE
            // ----------------------------------------------------
            else {
                response = await axios.post(baseUrl + "iot-devices/", payload);
            }
            // ----------------------------------------------------
            // SUCCESS
            // ----------------------------------------------------
            if (response.status === 200 || response.status === 201) {
                Swal.fire({
                    title: id ? "Device Updated" : "Device Registered",
                    text: "Agricultural IoT device saved successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });
                setTimeout(() => {
                    navigate("/iot-devices/");
                }, 1500);
            }
        } catch (error) {
            console.log("Save IoT Device Error:", error);
            let message = "Unable to save IoT device.";
            if (error.response && error.response.data) {
                console.log("Backend Response:", error.response.data);
            }
            Swal.fire("Error", message, "error");
        } finally {
            setLoading(false);
        }
    };
    // ============================================================
    // PAGE
    // ============================================================
    return (<section className="container mt-4">

            {/* ==================================================
                HEADER
            ================================================== */}

            <div className="iot-page-header">

                <div>

                    <h3>

                        {id ? "Edit Agricultural IoT Device" : "Register Agricultural IoT Device"}

                    </h3>

                    <p>
                        Configure sensors, controllers and
                        agricultural IoT devices
                    </p>

                </div>

            </div>


            <form onSubmit={submitHandler}>


                {/* ==================================================
                    DEVICE INFORMATION
                ================================================== */}

                <div className="card shadow-sm mb-4">

                    <div className="card-header">

                        <strong>
                            Device Information
                        </strong>

                    </div>


                    <div className="card-body">

                        <div className="row">


                            {/* DEVICE ID */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Device ID *
                                </label>

                                <input
                                    type="text"
                                    name="device_id"
                                    className="form-control"
                                    placeholder="SOIL-MOIST-001"
                                    value={formData.device_id}
                                    onChange={inputHandler}
                                    disabled={!!id}
                                />

                            </div>


                            {/* DEVICE NAME */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Device Name *
                                </label>

                                <input
                                    type="text"
                                    name="device_name"
                                    className="form-control"
                                    placeholder="Field 01 Soil Moisture Sensor"
                                    value={formData.device_name}
                                    onChange={inputHandler}
                                />

                            </div>

                        </div>


                        <div className="row">


                            {/* DEVICE TYPE */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Device Type *
                                </label>

                                <select
                                    name="device_type"
                                    className="form-control"
                                    value={formData.device_type}
                                    onChange={inputHandler}
                                >

                                    <option value="SOIL_MOISTURE">
                                        Soil Moisture Sensor
                                    </option>

                                    <option value="SOIL_TEMPERATURE">
                                        Soil Temperature Sensor
                                    </option>

                                    <option value="SOIL_PH">
                                        Soil pH Sensor
                                    </option>

                                    <option value="SOIL_EC">
                                        Soil EC Sensor
                                    </option>

                                    <option value="NPK_SENSOR">
                                        NPK Sensor
                                    </option>

                                    <option value="LEAF_WETNESS">
                                        Leaf Wetness Sensor
                                    </option>

                                    <option value="WEATHER_STATION">
                                        Weather Station
                                    </option>

                                    <option value="TEMPERATURE">
                                        Temperature Sensor
                                    </option>

                                    <option value="HUMIDITY">
                                        Humidity Sensor
                                    </option>

                                    <option value="RAINFALL">
                                        Rainfall Sensor
                                    </option>

                                    <option value="WIND_SPEED">
                                        Wind Speed Sensor
                                    </option>

                                    <option value="WIND_DIRECTION">
                                        Wind Direction Sensor
                                    </option>

                                    <option value="SOLAR_RADIATION">
                                        Solar Radiation Sensor
                                    </option>

                                    <option value="LIGHT_INTENSITY">
                                        Light Intensity Sensor
                                    </option>

                                    <option value="WATER_LEVEL">
                                        Water Level Sensor
                                    </option>

                                    <option value="WATER_FLOW">
                                        Water Flow Sensor
                                    </option>

                                    <option value="WATER_PRESSURE">
                                        Water Pressure Sensor
                                    </option>

                                    <option value="IRRIGATION_CONTROLLER">
                                        Irrigation Controller
                                    </option>

                                    <option value="PUMP_CONTROLLER">
                                        Pump Controller
                                    </option>

                                    <option value="VALVE_CONTROLLER">
                                        Valve Controller
                                    </option>

                                    <option value="CROP_MONITOR">
                                        Crop Monitoring Sensor
                                    </option>

                                    <option value="PLANT_HEALTH">
                                        Plant Health Sensor
                                    </option>

                                    <option value="PHEROMONE_TRAP">
                                        Smart Pheromone Trap
                                    </option>

                                    <option value="PEST_SENSOR">
                                        Pest Detection Sensor
                                    </option>

                                    <option value="CAMERA">
                                        Farm Camera
                                    </option>

                                    <option value="DRONE">
                                        Agricultural Drone
                                    </option>

                                    <option value="TRACTOR">
                                        Smart Tractor
                                    </option>

                                    <option value="HARVESTER">
                                        Smart Harvester
                                    </option>

                                    <option value="FARM_MACHINERY">
                                        Farm Machinery
                                    </option>

                                    <option value="GPS_TRACKER">
                                        GPS Tracker
                                    </option>

                                    <option value="LIVESTOCK_TRACKER">
                                        Livestock Tracker
                                    </option>

                                    <option value="LIVESTOCK_TEMPERATURE">
                                        Livestock Temperature Sensor
                                    </option>

                                    <option value="LIVESTOCK_HEALTH">
                                        Livestock Health Sensor
                                    </option>

                                    <option value="FEED_MONITOR">
                                        Feed Monitor
                                    </option>

                                    <option value="MILK_SENSOR">
                                        Milk Sensor
                                    </option>

                                    <option value="GREENHOUSE">
                                        Greenhouse Controller
                                    </option>

                                    <option value="COLD_STORAGE">
                                        Cold Storage Monitor
                                    </option>

                                    <option value="GRAIN_STORAGE">
                                        Grain Storage Monitor
                                    </option>

                                    <option value="SILO">
                                        Silo Monitor
                                    </option>

                                    <option value="ENERGY_METER">
                                        Energy Meter
                                    </option>

                                    <option value="SOLAR_CONTROLLER">
                                        Solar Controller
                                    </option>

                                    <option value="SECURITY_SENSOR">
                                        Farm Security Sensor
                                    </option>

                                    <option value="MOTION_SENSOR">
                                        Motion Sensor
                                    </option>

                                    <option value="GATEWAY">
                                        IoT Gateway
                                    </option>

                                    <option value="OTHER">
                                        Other
                                    </option>

                                </select>

                            </div>


                            {/* MANUFACTURER */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Manufacturer
                                </label>

                                <input
                                    type="text"
                                    name="manufacturer"
                                    className="form-control"
                                    value={formData.manufacturer}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* MODEL */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Model Number
                                </label>

                                <input
                                    type="text"
                                    name="model_number"
                                    className="form-control"
                                    value={formData.model_number}
                                    onChange={inputHandler}
                                />

                            </div>

                        </div>


                        <div className="row">


                            {/* SERIAL */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Serial Number
                                </label>

                                <input
                                    type="text"
                                    name="serial_number"
                                    className="form-control"
                                    value={formData.serial_number}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* FIRMWARE */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Firmware Version
                                </label>

                                <input
                                    type="text"
                                    name="firmware_version"
                                    className="form-control"
                                    placeholder="1.0.0"
                                    value={formData.firmware_version}
                                    onChange={inputHandler}
                                />

                            </div>

                        </div>

                    </div>

                </div>


                {/* ==================================================
                    COMMUNICATION
                ================================================== */}

                <div className="card shadow-sm mb-4">

                    <div className="card-header">

                        <strong>
                            Communication
                        </strong>

                    </div>


                    <div className="card-body">

                        <div className="row">


                            {/* PROTOCOL */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Protocol *
                                </label>

                                <select
                                    name="protocol"
                                    className="form-control"
                                    value={formData.protocol}
                                    onChange={inputHandler}
                                >

                                    <option value="MQTT">
                                        MQTT
                                    </option>

                                    <option value="HTTP">
                                        HTTP
                                    </option>

                                    <option value="HTTPS">
                                        HTTPS
                                    </option>

                                    <option value="TCP">
                                        TCP
                                    </option>

                                    <option value="UDP">
                                        UDP
                                    </option>

                                    <option value="MODBUS_TCP">
                                        Modbus TCP
                                    </option>

                                    <option value="MODBUS_RTU">
                                        Modbus RTU
                                    </option>

                                    <option value="RS485">
                                        RS-485
                                    </option>

                                    <option value="SERIAL">
                                        Serial
                                    </option>

                                    <option value="LORA">
                                        LoRa
                                    </option>

                                    <option value="LORAWAN">
                                        LoRaWAN
                                    </option>

                                    <option value="WIFI">
                                        Wi-Fi
                                    </option>

                                    <option value="BLUETOOTH">
                                        Bluetooth
                                    </option>

                                    <option value="BLE">
                                        Bluetooth Low Energy
                                    </option>

                                    <option value="ZIGBEE">
                                        Zigbee
                                    </option>

                                    <option value="NB_IOT">
                                        NB-IoT
                                    </option>

                                    <option value="LTE_M">
                                        LTE-M
                                    </option>

                                    <option value="GSM">
                                        GSM
                                    </option>

                                    <option value="OTHER">
                                        Other
                                    </option>

                                </select>

                            </div>


                            {/* IP */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    IP Address
                                </label>

                                <input
                                    type="text"
                                    name="ip_address"
                                    className="form-control"
                                    placeholder="192.168.1.100"
                                    value={formData.ip_address}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* PORT */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Port
                                </label>

                                <input
                                    type="number"
                                    name="port"
                                    className="form-control"
                                    placeholder="1883"
                                    value={formData.port}
                                    onChange={inputHandler}
                                />

                            </div>

                        </div>


                        <div className="row">


                            {/* MAC */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    MAC Address
                                </label>

                                <input
                                    type="text"
                                    name="mac_address"
                                    className="form-control"
                                    placeholder="AA:BB:CC:DD:EE:FF"
                                    value={formData.mac_address}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* GATEWAY */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    IoT Gateway
                                </label>

                                <select
                                    name="gateway"
                                    className="form-control"
                                    value={formData.gateway}
                                    onChange={inputHandler}
                                >

                                    <option value="">
                                        Select Gateway
                                    </option>

                                    {gateways.map(gateway => (<option
                                            key={gateway.id}
                                            value={gateway.id}
                                        >
                                            {gateway.gateway_name}

                                            {" - "}

                                            {gateway.gateway_id}

                                        </option>))}

                                </select>

                            </div>

                        </div>

                    </div>

                </div>


                {/* ==================================================
                    DEVICE STATUS
                ================================================== */}

                <div className="card shadow-sm mb-4">

                    <div className="card-header">

                        <strong>
                            Device Status
                        </strong>

                    </div>


                    <div className="card-body">

                        <div className="row">

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Status
                                </label>

                                <select
                                    name="status"
                                    className="form-control"
                                    value={formData.status}
                                    onChange={inputHandler}
                                >

                                    <option value="ACTIVE">
                                        Active
                                    </option>

                                    <option value="INACTIVE">
                                        Inactive
                                    </option>

                                    <option value="MAINTENANCE">
                                        Maintenance
                                    </option>

                                </select>

                            </div>

                        </div>

                    </div>

                </div>


                {/* ==================================================
                    MAINTENANCE
                ================================================== */}

                <div className="card shadow-sm mb-4">

                    <div className="card-header">

                        <strong>
                            Installation & Maintenance
                        </strong>

                    </div>


                    <div className="card-body">

                        <div className="row">


                            {/* INSTALLATION */}

                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Installation Date
                                </label>

                                <input
                                    type="date"
                                    name="installation_date"
                                    className="form-control"
                                    value={formData.installation_date}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* WARRANTY */}

                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Warranty Expiry
                                </label>

                                <input
                                    type="date"
                                    name="warranty_expiry"
                                    className="form-control"
                                    value={formData.warranty_expiry}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* CALIBRATION */}

                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Calibration Due
                                </label>

                                <input
                                    type="date"
                                    name="calibration_due"
                                    className="form-control"
                                    value={formData.calibration_due}
                                    onChange={inputHandler}
                                />

                            </div>


                            {/* SERVICE */}

                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Service Due
                                </label>

                                <input
                                    type="date"
                                    name="service_due"
                                    className="form-control"
                                    value={formData.service_due}
                                    onChange={inputHandler}
                                />

                            </div>

                        </div>

                    </div>

                </div>


                {/* ==================================================
                    ACTIONS
                ================================================== */}

                <div className="text-center mb-5">

                    <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => navigate("/iot-devices/")}
                    >
                        Cancel
                    </button>


                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={loading}
                    >

                        {loading ? "Saving..." : id ? "Update Device" : "Register Device"}

                    </button>

                </div>

            </form>

        </section>);
}
export default IoTDeviceForm;
